import {
  ADMIN_API,
  adminAddAdminSchema,
  adminAuditQuerySchema,
  adminRestaurantsQuerySchema,
  adminSetPlanSchema,
  adminSetStatusSchema,
  adminTableQuerySchema,
} from '@adop/shared/admin';
import type {
  AdminAddAdminRequest,
  AdminAdminsResponse,
  AdminAuditQuery,
  AdminAuditResponse,
  AdminMeResponse,
  AdminMutationResponse,
  AdminOverviewResponse,
  AdminRestaurantDetailResponse,
  AdminRestaurantsQuery,
  AdminRestaurantsResponse,
  AdminRestaurantStatusResponse,
  AdminSetPlanRequest,
  AdminSetPlanResponse,
  AdminSetStatusRequest,
  AdminTablePageResponse,
  AdminTableQuery,
  AdminTablesResponse,
} from '@adop/shared/admin';
import { apiFetch, postJson } from './api';

/** Successful response for POST /admin/admins (the shared file only types `ok`). */
export interface AdminAddAdminResponse extends AdminMutationResponse {
  user_id: string;
  email: string;
}

type QueryValue = string | number | boolean | undefined;

/**
 * Serialises a flat record into a query string, dropping `undefined` and empty
 * string values so optional filters never show up as `?q=`. Returns `''` when
 * there is nothing to append.
 */
export function toAdminQuery(entries: Record<string, QueryValue>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(entries)) {
    if (value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const query = params.toString();
  return query.length > 0 ? `?${query}` : '';
}

/** GET /admin/me — safe for every signed-in user; returns `is_admin: false` for non-admins. */
export function fetchAdminMe(): Promise<AdminMeResponse> {
  return apiFetch<AdminMeResponse>(ADMIN_API.me);
}

/** GET /admin/overview — platform-wide totals plus recent activity. */
export function fetchAdminOverview(): Promise<AdminOverviewResponse> {
  return apiFetch<AdminOverviewResponse>(ADMIN_API.overview);
}

/** GET /admin/restaurants — paginated restaurant list with optional search. */
export function fetchAdminRestaurants(
  query: Partial<AdminRestaurantsQuery> = {},
): Promise<AdminRestaurantsResponse> {
  const parsed = adminRestaurantsQuerySchema.parse(query);
  return apiFetch<AdminRestaurantsResponse>(`${ADMIN_API.restaurants}${toAdminQuery(parsed)}`);
}

/** GET /admin/restaurants/:id — opening this support view is audited server-side. */
export function fetchAdminRestaurant(id: string): Promise<AdminRestaurantDetailResponse> {
  return apiFetch<AdminRestaurantDetailResponse>(ADMIN_API.restaurant(id));
}

/** POST /admin/restaurants/:id/status — suspend or reactivate a restaurant. */
export function setAdminRestaurantStatus(
  id: string,
  body: AdminSetStatusRequest,
): Promise<AdminRestaurantStatusResponse> {
  const parsed = adminSetStatusSchema.parse(body);
  return postJson<AdminRestaurantStatusResponse>(ADMIN_API.restaurantStatus(id), parsed);
}

/** POST /admin/restaurants/:id/plan — change a restaurant's plan (audited). */
export function setRestaurantPlan(
  id: string,
  body: AdminSetPlanRequest,
): Promise<AdminSetPlanResponse> {
  const parsed = adminSetPlanSchema.parse(body);
  return postJson<AdminSetPlanResponse>(ADMIN_API.restaurantPlan(id), parsed);
}

/** GET /admin/tables — read-only allow-list of browsable tables. */
export function fetchAdminTables(): Promise<AdminTablesResponse> {
  return apiFetch<AdminTablesResponse>(ADMIN_API.tables);
}

/** GET /admin/tables/:table — one read-only page of a table. */
export function fetchAdminTable(
  name: string,
  query: Partial<AdminTableQuery> = {},
): Promise<AdminTablePageResponse> {
  const { desc, ...rest } = query;
  const parsed = adminTableQuerySchema.parse({
    ...rest,
    ...(desc === undefined ? {} : { desc: desc ? 'true' : 'false' }),
  });
  return apiFetch<AdminTablePageResponse>(`${ADMIN_API.table(name)}${toAdminQuery(parsed)}`);
}

/** GET /admin/audit — paginated audit feed with optional filters. */
export function fetchAdminAudit(query: Partial<AdminAuditQuery> = {}): Promise<AdminAuditResponse> {
  const parsed = adminAuditQuerySchema.parse(query);
  return apiFetch<AdminAuditResponse>(`${ADMIN_API.audit}${toAdminQuery(parsed)}`);
}

/** GET /admin/admins — current platform admin roster. */
export function fetchAdminAdmins(): Promise<AdminAdminsResponse> {
  return apiFetch<AdminAdminsResponse>(ADMIN_API.admins);
}

/** POST /admin/admins — promote an existing auth user by email. */
export function addPlatformAdmin(body: AdminAddAdminRequest): Promise<AdminAddAdminResponse> {
  const parsed = adminAddAdminSchema.parse(body);
  return postJson<AdminAddAdminResponse>(ADMIN_API.admins, parsed);
}

/** DELETE /admin/admins/:userId — the API refuses to remove the last admin. */
export function removePlatformAdmin(userId: string): Promise<AdminMutationResponse> {
  return apiFetch<AdminMutationResponse>(ADMIN_API.admin(userId), { method: 'DELETE' });
}
